/**
 * Pure view-model of the temper (upgrade) screen (DOM-free, unit-tested in temperModel.test.ts):
 * the chance breakdown chips, the Resonance meter, "guaranteed within N", outcome copy that makes
 * a failure read as progress ("Resonance +35% · refunded 2 Jade, 6 Fangsteel"), the item list
 * and the KMMO Enhance view (enhanceView / enhancePreview / enhanceDiffs). Numbers come from progression's pure temperOdds / masterworkOdds.
 */
import type { ClassDef, ClassId } from '@keystone/sim';
import {
  type ChanceBreakdown,
  type ForgeRoll,
  type Item,
  type MasterworkOdds,
  type Profile,
  type TemperBlock,
  type TemperOdds,
  AWAKEN_TABLE,
  FAILSTACK_PP,
  ITEM_SLOTS,
  RESONANCE_MAX,
  RESONANCE_NEAR_MISS,
  RESONANCE_PER_FAIL,
  effectiveIlvl,
  equippedGear,
  itemOwner,
  masterworkItem,
  masterworkOdds,
  rescaleSecondaries,
  temperOdds,
} from '@keystone/progression';
import type { RollBoosts } from '@keystone/progression';
import { type ResourceKey, RESOURCE_META, SCALE_LABEL } from '../forge/resourceText';
import { compileFor, gearWith, sheetStats } from './characterModel';
import { STAT_LABEL } from '../ui/itemText';
import { bundleText, shortfallText } from '../forge/resourceText';
import { formatPct } from './forgeModel';

export interface ChanceChip {
  label: string;
  /** Percentage points (base is the raw %). */
  pp: number;
  text: string;
}

/** "Base 55%", "Failstack +20%", "Venom +15%", "Blessed +15%". */
export function chanceChips(o: ChanceBreakdown): ChanceChip[] {
  const chips: ChanceChip[] = [{ label: 'Base', pp: o.base, text: `Base ${formatPct(o.base)}` }];
  if (o.failstack) chips.push({ label: 'Failstack', pp: o.failstack, text: `Failstack +${o.failstack}%` });
  if (o.venom) chips.push({ label: 'Venom', pp: o.venom, text: `Venom +${o.venom}%` });
  if (o.blessed) chips.push({ label: 'Blessed', pp: o.blessed, text: `Blessed +${o.blessed}%` });
  if (o.guaranteed) chips.push({ label: 'Resonance', pp: 100 - o.base, text: 'Resonance full: guaranteed' });
  return chips;
}

export interface ResonanceView {
  value: number;
  max: number;
  fill: number;
  full: boolean;
  label: string;
  /** What one more fail adds ("+25%, +35% on a near miss"). */
  perFail: string;
}

export function resonanceView(resonance: number): ResonanceView {
  const v = Math.max(0, Math.min(RESONANCE_MAX, resonance));
  const full = v >= RESONANCE_MAX;
  return {
    value: v,
    max: RESONANCE_MAX,
    fill: v / RESONANCE_MAX,
    full,
    label: full ? 'Resonance full: next attempt guaranteed' : `Resonance ${v}%`,
    perFail: `+${RESONANCE_PER_FAIL}% per fail, +${RESONANCE_NEAR_MISS}% on a near miss`,
  };
}

/** "Guaranteed within 3 tries" / "Guaranteed". */
export function guaranteeText(n: number): string {
  return n <= 1 ? 'Guaranteed this try' : `Guaranteed within ${n} tries`;
}

const TEMPER_BLOCK: Readonly<Record<Exclude<TemperBlock, 'cantAfford' | 'notEnoughGold'>, string>> = {
  unknownItem: 'That item no longer exists.',
  capped: 'At your rank’s item level cap. Masterwork it, or climb a rank to temper further.',
  badReagent: 'Those boosts can’t be used here.',
};

export function temperBlockText(o: Pick<TemperOdds, 'blocked' | 'shortfall'>): string | null {
  if (!o.blocked) return null;
  if (o.blocked === 'cantAfford' || o.blocked === 'notEnoughGold') return shortfallText(o.shortfall) ?? 'Not enough resources.';
  return TEMPER_BLOCK[o.blocked];
}

export interface TemperPanelView {
  chance: number;
  chanceLabel: string;
  chips: ChanceChip[];
  resonance: ResonanceView;
  guarantee: string;
  /** "≈ 1.6 tries on average". */
  expected: string;
  /** P(success) by attempt 1..5 as % labels. */
  byAttempt: string[];
  from: number;
  target: number;
  surge: string | null;
  awakening: string | null;
  costText: string;
  refundText: string;
  failstackNext: string;
  blocked: string | null;
}

export function temperPanel(o: TemperOdds): TemperPanelView {
  const awakening = o.awakening ? `This step triggers an Awakening: ${AWAKEN_TABLE.map((a) => `${cap(a.kind)} ${Math.round(a.chance * 100)}%`).join(' · ')}` : null;
  return {
    chance: o.odds.chance,
    chanceLabel: formatPct(o.odds.chance),
    chips: chanceChips(o.odds),
    resonance: resonanceView(o.resonance),
    guarantee: guaranteeText(o.guaranteedWithin),
    expected: `≈ ${o.expected.attempts.toFixed(1)} tries on average`,
    byAttempt: o.expected.byAttempt.slice(0, 5).map((p) => formatPct(Math.round(p * 1000) / 10)),
    from: o.from,
    target: o.target,
    surge: o.surgeTarget !== null ? `${Math.round(o.surgeChance * 100)}% of successes Surge to ${o.surgeTarget}` : null,
    awakening,
    costText: bundleText(o.cost, 'Free'),
    refundText: bundleText(o.refundOnFail, 'nothing'),
    failstackNext: `+${FAILSTACK_PP}% next try on a fail`,
    blocked: temperBlockText(o),
  };
}

export function masterworkPanel(o: MasterworkOdds): { chanceLabel: string; chips: ChanceChip[]; guarantee: string; costText: string; refundText: string; blocked: string | null; step: number; ceiling: number } {
  const blocked = !o.blocked
    ? null
    : o.blocked === 'notAtCap'
      ? 'Temper to your rank’s item level cap first.'
      : o.blocked === 'maxMasterwork'
        ? `At this rarity’s masterwork ceiling (+${o.ceiling}). Ascend it with an Awakening to go higher.`
        : o.blocked === 'cantAfford' || o.blocked === 'notEnoughGold'
          ? (shortfallText(o.shortfall) ?? 'Not enough resources.')
          : o.blocked === 'unknownItem'
            ? 'That item no longer exists.'
            : 'Those boosts can’t be used here.';
  return { chanceLabel: formatPct(o.odds.chance), chips: chanceChips(o.odds), guarantee: guaranteeText(o.guaranteedWithin), costText: bundleText(o.cost, 'Free'), refundText: bundleText(o.refundOnFail, 'nothing'), blocked, step: o.step, ceiling: o.ceiling };
}

const cap = (s: string): string => s[0]!.toUpperCase() + s.slice(1);

// ---------------------------------------------------------------------------
// Outcome copy
// ---------------------------------------------------------------------------

export type OutcomeTone = 'success' | 'surge' | 'fail' | 'near';

export interface OutcomeText {
  tone: OutcomeTone;
  headline: string;
  /** "rolled 58 · needed ≤ 55". */
  rollLine: string;
  /** Failure as progress: "Resonance +35% (near miss)". Null on success. */
  resonanceLine: string | null;
  refundLine: string | null;
  /** "+10% next try" chip on a fail. */
  nextLine: string | null;
  /** "Item level 230 → 235". */
  ilvlLine: string | null;
  awakeningLine: string | null;
}

const AWAKEN_TEXT: Readonly<Record<string, string>> = {
  empowered: 'Empowered: a secondary stat gains +15%',
  socket: 'Socket: a new gem socket',
  echo: 'Echo: this step’s Scales refunded, +2 Venom',
  ascend: 'Ascend: rarity +1',
};

export function outcomeText(roll: ForgeRoll): OutcomeText {
  const rollLine = roll.odds.guaranteed ? `guaranteed by Resonance (rolled ${roll.r})` : `rolled ${roll.r} · needed ≤ ${roll.needed}`;
  const what = roll.kind === 'temper' ? 'Temper' : roll.kind === 'masterwork' ? 'Masterwork' : roll.kind === 'drill' ? 'Drill' : 'Gilding';
  if (roll.outcome === 'fail') {
    const gain = roll.resonanceAfter - roll.resonanceBefore;
    const resonanceLine = roll.resonanceAfter >= RESONANCE_MAX ? `Resonance +${gain}% · FULL: next try is guaranteed` : `Resonance +${gain}%${roll.nearMiss ? ' (near miss)' : ''}`;
    const refund = bundleText(roll.refund, '');
    return {
      tone: roll.nearMiss ? 'near' : 'fail',
      headline: roll.nearMiss ? `So close: rolled ${roll.r}, needed ≤ ${roll.needed}` : `${what} failed: the item is untouched`,
      rollLine,
      resonanceLine,
      refundLine: refund ? `Refunded ${refund}` : null,
      nextLine: roll.kind === 'temper' || roll.kind === 'masterwork' ? `+${FAILSTACK_PP}% next try` : null,
      ilvlLine: null,
      awakeningLine: null,
    };
  }
  const surge = roll.outcome === 'surge';
  const ilvlLine = roll.ilvlAfter !== roll.ilvlBefore ? `Item level ${roll.ilvlBefore} → ${roll.ilvlAfter}${surge ? ` (+${roll.ilvlAfter - roll.ilvlBefore})` : ''}` : null;
  return {
    tone: surge ? 'surge' : 'success',
    headline: surge ? `SURGE! Two steps for one` : `${what} succeeded`,
    rollLine,
    resonanceLine: null,
    refundLine: null,
    nextLine: null,
    ilvlLine,
    awakeningLine: roll.awakening ? `AWAKENING: ${AWAKEN_TEXT[roll.awakening.kind] ?? roll.awakening.kind}${roll.awakening.rolled !== roll.awakening.kind ? ` (rolled ${cap(roll.awakening.rolled)}, which could not apply)` : ''}` : null,
  };
}

// ---------------------------------------------------------------------------
// Item list
// ---------------------------------------------------------------------------

export interface TemperTarget {
  item: Item;
  ilvl: number;
  /** Can temper now (else at cap: masterwork). */
  mode: 'temper' | 'masterwork' | 'done';
  chance: number;
  affordable: boolean;
  resonance: number;
}

/** A hero's equipped items, lowest item level first, with their next roll's state. */
export function temperTargets(p: Profile, classId: ClassId): TemperTarget[] {
  const gear = equippedGear(p, classId);
  const out: TemperTarget[] = [];
  for (const slot of ITEM_SLOTS) {
    const item = gear[slot];
    if (!item) continue;
    const t = temperOdds(p, item, { useFree: true });
    if (t.blocked !== 'capped') {
      out.push({ item, ilvl: effectiveIlvl(item), mode: 'temper', chance: t.odds.chance, affordable: !t.blocked, resonance: t.resonance });
      continue;
    }
    const m = masterworkOdds(p, item, { useFree: true });
    const done = m.blocked === 'maxMasterwork';
    out.push({ item, ilvl: effectiveIlvl(item), mode: done ? 'done' : 'masterwork', chance: m.odds.chance, affordable: !m.blocked, resonance: item.forge?.resonance ?? 0 });
  }
  return out.sort((a, b) => a.ilvl - b.ilvl || ITEM_SLOTS.indexOf(a.item.slot) - ITEM_SLOTS.indexOf(b.item.slot));
}

/** "Temper ×N" summary: worst-case spend for up to n attempts and what stops it. */
export function temperManyText(o: TemperOdds, n: number): string {
  const tries = Math.min(n, o.guaranteedWithin);
  return `Rolls until a success (at most ${tries} ${tries === 1 ? 'try' : 'tries'}), stopping for any Surge or Awakening. Each try costs ${bundleText(o.cost, 'nothing')}; a fail refunds ${bundleText(o.refundOnFail, 'nothing')}.`;
}

/** Whose item it is ("" when it is in the bag). */
export function wearer(p: Profile, item: Item): ClassId | null {
  return itemOwner(p, item.id);
}

// ---------------------------------------------------------------------------
// Enhance (KMMO Forge › Enhance): one view over temper + masterwork
// ---------------------------------------------------------------------------

export interface EnhanceMaterial {
  key: ResourceKey;
  /** Painted icon stem. */
  icon: string;
  label: string;
  have: number;
  need: number;
}

export interface EnhanceView {
  /** 'temper' below the tier cap, 'masterwork' at it, 'max' at the masterwork ceiling. */
  mode: 'temper' | 'masterwork' | 'max';
  /** Current "+N" (temper steps + masterwork). */
  level: number;
  ilvlFrom: number;
  ilvlTo: number;
  /** Success chance, 0..100 (Blessing included when applied). */
  chance: number;
  /** Percentage points the Blessing adds (0 when not applied). */
  blessedPP: number;
  /** A Blessed Charge could raise the chance (have one, under 100%). */
  canBless: boolean;
  gold: number;
  free: boolean;
  /** Fangsteel + the Scale grade(s) actually paid, with have / need (at most 3). */
  materials: EnhanceMaterial[];
  /** Resonance as fails toward the guarantee ("2/4"). */
  resonance: { steps: number; max: number; full: boolean };
  /** "+10% after a failed attempt". */
  failNote: string;
  /** This step rolls an Awakening on success. */
  awakening: boolean;
  /** Why the button is disabled (short), or null. */
  blocked: string | null;
  /** Options passed to temper / masterworkItem for exactly these numbers. */
  opts: RollBoosts & { useFree: true };
}

const SCALE_ICON: Readonly<Record<string, string>> = { ashen: 'forge_scale_ashen', jade: 'forge_scale_jade', gilded: 'forge_scale_gilded', eternal: 'forge_scale_eternal' };

function materialsOf(p: Profile, cost: { fangsteel?: number; scales?: Partial<Record<string, number>> }): EnhanceMaterial[] {
  const out: EnhanceMaterial[] = [];
  if (cost.fangsteel) out.push({ key: 'fangsteel', icon: 'forge_fangsteel', label: RESOURCE_META.fangsteel.label, have: p.wallet.fangsteel, need: cost.fangsteel });
  for (const g of ['ashen', 'jade', 'gilded', 'eternal'] as const) {
    const n = cost.scales?.[g] ?? 0;
    if (n > 0) out.push({ key: `scale.${g}`, icon: SCALE_ICON[g]!, label: `${SCALE_LABEL[g]} Scale`, have: p.wallet.scales[g], need: n });
  }
  return out.slice(0, 3);
}

/**
 * Everything the Enhance view shows for one item. `bless`: apply a Blessed Charge when it helps
 * (the player can switch it off for the session).
 */
export function enhanceView(p: Profile, item: Item, bless = true): EnhanceView {
  const level = (item.forge?.steps ?? 0) + (item.masterwork ?? 0);
  const base = { useFree: true as const };
  const t0 = temperOdds(p, item, base);
  const resonance = (r: number): EnhanceView['resonance'] => {
    const max = Math.ceil(RESONANCE_MAX / RESONANCE_PER_FAIL);
    return { steps: Math.min(max, Math.round(r / RESONANCE_PER_FAIL)), max, full: r >= RESONANCE_MAX };
  };
  const failNote = `+${FAILSTACK_PP}% after a failed attempt`;
  if (t0.blocked !== 'capped') {
    const canBless = p.wallet.blessed > 0 && t0.odds.chance < 100;
    const opts = canBless && bless ? { ...base, blessed: true } : base;
    const t = opts === base ? t0 : temperOdds(p, item, opts);
    return {
      mode: 'temper',
      level,
      ilvlFrom: effectiveIlvl(item),
      ilvlTo: effectiveIlvl(item) + (t.target - t.from),
      chance: t.odds.chance,
      blessedPP: t.odds.blessed,
      canBless,
      gold: t.cost.gold ?? 0,
      free: t.free,
      materials: materialsOf(p, t.free ? temperOdds(p, item, { ...opts, useFree: false }).cost : t.cost),
      resonance: resonance(t.resonance),
      failNote,
      awakening: t.awakening,
      blocked: temperBlockText(t),
      opts,
    };
  }
  const m0 = masterworkOdds(p, item, base);
  const canBless = p.wallet.blessed > 0 && m0.odds.chance < 100 && m0.blocked !== 'maxMasterwork';
  const opts = canBless && bless ? { ...base, blessed: true } : base;
  const m = opts === base ? m0 : masterworkOdds(p, item, opts);
  const max = m.blocked === 'maxMasterwork';
  return {
    mode: max ? 'max' : 'masterwork',
    level,
    ilvlFrom: effectiveIlvl(item),
    ilvlTo: effectiveIlvl(item) + (max ? 0 : 1),
    chance: m.odds.chance,
    blessedPP: m.odds.blessed,
    canBless,
    gold: m.cost.gold ?? 0,
    free: m.free,
    materials: max ? [] : materialsOf(p, m.free ? masterworkOdds(p, item, { ...opts, useFree: false }).cost : m.cost),
    resonance: resonance(item.forge?.resonance ?? 0),
    failNote,
    awakening: false,
    blocked: max ? 'MAX' : masterworkPanel(m).blocked,
    opts,
  };
}

/** The item as it would be after one successful step (no Surge, no Awakening). */
export function enhancePreview(item: Item, v: Pick<EnhanceView, 'mode' | 'ilvlTo' | 'ilvlFrom'>): Item {
  if (v.mode === 'masterwork') return { ...item, masterwork: (item.masterwork ?? 0) + 1 };
  if (v.mode === 'max') return item;
  const ilvl = item.ilvl + (v.ilvlTo - v.ilvlFrom);
  const forge = { ...(item.forge ?? { steps: 0, fails: 0, resonance: 0, invested: 0 }), steps: (item.forge?.steps ?? 0) + 1 } as NonNullable<Item['forge']>;
  return { ...item, ilvl, forge, secondaries: rescaleSecondaries(item, ilvl) };
}

export interface EnhanceDiff {
  label: string;
  from: number;
  to: number;
}

/**
 * Up to 3 rows for "+4 → +5": Item Level, then the wearer's Power (when worn) and the item's
 * biggest secondary rating.
 */
export function enhanceDiffs(p: Profile, def: ClassDef | null, item: Item, after: Item): EnhanceDiff[] {
  const rows: EnhanceDiff[] = [{ label: 'Item Level', from: effectiveIlvl(item), to: effectiveIlvl(after) }];
  const owner = itemOwner(p, item.id);
  if (def && owner === def.id) {
    try {
      const gear = equippedGear(p, def.id);
      const a = sheetStats(compileFor(p, def, gear));
      const b = sheetStats(compileFor(p, def, gearWith(gear, after)));
      rows.push({ label: 'Power', from: Math.round(a.power), to: Math.round(b.power) });
    } catch {
      /* compile failure: just the item rows */
    }
  }
  let best = -1;
  item.secondaries.forEach((s, i) => {
    if (best < 0 || s.rating > item.secondaries[best]!.rating) best = i;
  });
  const s0 = item.secondaries[best];
  const s1 = after.secondaries[best];
  if (s0 && s1) rows.push({ label: STAT_LABEL[s0.stat], from: s0.rating, to: s1.rating });
  return rows.slice(0, 3);
}

/** Masterwork repeatedly (Auto-enhance at the cap): stops on the first success, a block, or `max` tries. */
export function masterworkUntil(profile: Profile, itemId: string, opts: RollBoosts & { useFree?: boolean }, max = 5): { profile: Profile; rolls: ForgeRoll[]; blocked: string | null } {
  let p = profile;
  const rolls: ForgeRoll[] = [];
  for (let i = 0; i < max; i++) {
    const r = masterworkItem(p, itemId, opts);
    if (!r.ok) return { profile: p, rolls, blocked: r.reason };
    p = r.profile;
    rolls.push(r.roll);
    if (r.roll.outcome !== 'fail') break;
  }
  return { profile: p, rolls, blocked: null };
}
